import React from 'react';
import { Clock, TrendingUp, BarChart3, ChevronRight, Zap, User, EyeOff, Hash, Server } from 'lucide-react';
import { formatDistanceToNow, format, isPast, parseISO } from 'date-fns';
import { Card, StatusBadge } from '../ui';
import type { Prediction } from '../../types';

interface PredictionCardProps {
  prediction: Prediction;
  onClick: () => void;
}

export function PredictionCard({ prediction, onClick }: PredictionCardProps) {
  const isExpired = prediction.deadline && isPast(parseISO(prediction.deadline));
  const totalVotes = prediction.votes.success.length + prediction.votes.fail.length;

  return (
    <Card
      hoverable
      onClick={onClick}
      className="group relative"
    >
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-foretell-accent">
        <Zap size={16} fill="currentColor" />
      </div>

      <div className="flex justify-between items-start mb-4">
        <StatusBadge status={prediction.status} />
        {prediction.deadline && (
          <span className="text-[10px] font-mono font-bold text-slate-500 flex items-center gap-1.5">
            <Clock size={10} />
            {isExpired ? 'Expired' : format(parseISO(prediction.deadline), 'MMM d, yyyy')}
          </span>
        )}
      </div>

      <h3 className="text-lg font-bold leading-tight mb-4 group-hover:text-foretell-accent transition-colors italic">
        "{prediction.text}"
      </h3>

      <div className="flex items-center justify-between border-t border-white/10 pt-4">
        <div className="flex items-center gap-4 text-[10px] font-bold text-slate-400 tracking-wider">
          <div className="flex items-center gap-1.5">
            <TrendingUp size={14} className="text-foretell-accent" />
            {prediction.confidence}%
          </div>
          <div className="flex items-center gap-1.5">
            <BarChart3 size={14} />
            {totalVotes} votes
          </div>
          {prediction.isAnonymous ? (
            <div className="flex items-center gap-1.5 text-white/30">
              <EyeOff size={14} />
              Anonymous
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              {prediction.authorAvatar ? (
                <img
                  src={`https://cdn.discordapp.com/avatars/${prediction.authorId.replace('discord_', '')}/${prediction.authorAvatar}.png?size=16`}
                  alt=""
                  className="w-4 h-4 rounded-full"
                />
              ) : (
                <User size={14} />
              )}
              {prediction.authorName}
            </div>
          )}
        </div>
        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-foretell-accent/20 transition-colors">
          <ChevronRight size={18} className="text-white/20 group-hover:text-white transition-colors" />
        </div>
      </div>
      <div className="flex items-center gap-4 text-[9px] font-mono text-slate-600 mt-2 pt-2 border-t border-white/5">
        <div className="flex items-center gap-1">
          <Server size={10} />
          <span>{prediction.serverId}</span>
        </div>
        <div className="flex items-center gap-1">
          <Hash size={10} />
          <span>{prediction.channelId}</span>
        </div>
      </div>
    </Card>
  );
}

interface PredictionListProps {
  predictions: Prediction[];
  onSelect: (prediction: Prediction) => void;
  emptyMessage?: string;
}

export function PredictionList({ predictions, onSelect, emptyMessage }: PredictionListProps) {
  if (predictions.length === 0) {
    return (
      <Card className="py-16 text-center border-dashed">
        <div className="text-slate-500 mb-2">
          <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
        </div>
        <p className="text-sm font-bold text-slate-500 tracking-wider uppercase">
          {emptyMessage || 'No predictions yet'}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {predictions.map((prediction) => (
        <PredictionCard
          key={prediction.id}
          prediction={prediction}
          onClick={() => onSelect(prediction)}
        />
      ))}
    </div>
  );
}
